import { ISDUIResponsiveValue } from "@faire/deprecated-retailer-visitor-shared/components/SDUI/__internal__/Styling/responsiveValues";
import { SDUILayoutRendererItem } from "@faire/deprecated-retailer-visitor-shared/components/SDUI/LayoutRenderers/types";
import { IResponsiveComponentLayoutConfig } from "@faire/web-api--source/indigofair/data/IResponsiveComponentLayoutConfig";
import { isNotUndefined } from "@faire/web/common/typescriptUtils";
import { Viewport } from "@faire/web/ui/hooks/viewport-utils/viewport-utils";

export type ItemNodeProps = {
  key: string;
  itemColSpans: ISDUIResponsiveValue<number>;
  item: SDUILayoutRendererItem;
  itemOrder: ISDUIResponsiveValue<number> | undefined;
  enableLoadingPlaceholder: boolean | undefined;
  rowSpacingClasses?: string[];
};

export const getBatchedItemNodePropsLists = (
  componentLayoutConfigs: IResponsiveComponentLayoutConfig[],
  itemNodePropsList: ItemNodeProps[],
  numFirstRows: number
) => {
  // Track items in the first rows
  const firstRowsIndices = new Set<number>();
  let currentRowWidth = 0;
  let rowCount = 0;

  // Iterate through component layout configs to determine first batch of rows
  for (const config of componentLayoutConfigs) {
    if (
      config.component_index === undefined ||
      config.component_width === undefined
    ) {
      continue;
    }

    // If adding this component would exceed 100% width, start a new row
    if (currentRowWidth + config.component_width > 100) {
      currentRowWidth = config.component_width;
      rowCount++;
    } else {
      currentRowWidth += config.component_width;
    }

    // Add component to first rows if we're still within the first batch of rows
    if (rowCount < numFirstRows) {
      firstRowsIndices.add(config.component_index);
    }
  }

  // Split the itemNodes into first rows and rest
  const firstRowsItemNodePropsList: ItemNodeProps[] = [];
  const restItemNodePropsList: ItemNodeProps[] = [];

  // Distribute itemNodes based on the indices we collected
  itemNodePropsList.forEach((itemNodeProps, index) => {
    if (firstRowsIndices.has(index)) {
      firstRowsItemNodePropsList.push(itemNodeProps);
    } else {
      restItemNodePropsList.push(itemNodeProps);
    }
  });

  return { firstRowsItemNodePropsList, restItemNodePropsList };
};

export const getComponentLayoutConfigsByViewport = (
  responseiveLayoutConfigs: ISDUIResponsiveValue<
    IResponsiveComponentLayoutConfig[]
  >,
  viewport: Viewport
) => {
  if (viewport.isXLargeAndAbove && responseiveLayoutConfigs.x_large_and_above) {
    return responseiveLayoutConfigs.x_large_and_above;
  } else if (
    viewport.isDesktopAndAbove &&
    responseiveLayoutConfigs.desktop_and_above
  ) {
    return responseiveLayoutConfigs.desktop_and_above;
  } else if (
    viewport.isTabletAndAbove &&
    responseiveLayoutConfigs.tablet_and_above
  ) {
    return responseiveLayoutConfigs.tablet_and_above;
  } else {
    return responseiveLayoutConfigs.mobile_and_above;
  }
};

export const getSortedItemsByOrderAndWidth = (
  items: SDUILayoutRendererItem[],
  orders: number[] | undefined,
  widths: (number | undefined)[] | undefined
): SDUILayoutRendererItem[] => {
  if (!orders?.length || !widths?.length) {
    return [...items];
  }

  // Pre-compute order values to avoid repeated calculations
  const orderValues = new Map<number, number>();
  items.forEach((_, index) => {
    orderValues.set(
      index,
      getItemOrderWithFullWidthComponentsLast(index, orders, widths)
    );
  });

  // Create array of indices for stable sort
  const indices = items.map((_, i) => i);

  // Sort items based on pre-computed order values
  indices.sort((a, b) => {
    const orderA = orderValues.get(a) ?? 0;
    const orderB = orderValues.get(b) ?? 0;
    return orderA - orderB;
  });

  // Return items in sorted order
  return indices.map((i) => items[i]).filter(isNotUndefined);
};

const getItemOrderWithFullWidthComponentsLast = (
  index: number,
  orders: number[],
  widths: (number | undefined)[]
): number => {
  const width = index < widths.length ? widths[index] : 100;
  const order = (index < orders.length ? orders[index] : index) ?? 0;
  // Move full width items to the end. This is to prevent full width components
  // from offsetting the position of non-full-width components when rendering.
  return width === undefined || width === 100 ? orders.length + order : order;
};

// Sort orders, widths, and layout configs to match the sorted items for each viewport
export const getResponsiveSortedOrdersAndWidthsAndLayoutConfigs = (
  sortedItems: SDUILayoutRendererItem[],
  originalItems: SDUILayoutRendererItem[],
  orders: ISDUIResponsiveValue<number[]>,
  widths: ISDUIResponsiveValue<(number | undefined)[]>,
  layoutConfigs: ISDUIResponsiveValue<IResponsiveComponentLayoutConfig[]>
): {
  responsiveSortedOrders: ISDUIResponsiveValue<number[]>;
  responsiveSortedWidths: ISDUIResponsiveValue<(number | undefined)[]>;
  responsiveSortedConfigs: ISDUIResponsiveValue<
    IResponsiveComponentLayoutConfig[]
  >;
} => {
  const responsiveSortedOrders = { ...orders };
  const responsiveSortedWidths = { ...widths };
  const responsiveSortedConfigs = { ...layoutConfigs };

  const mobileResult = getSortedOrdersAndWidthsAndLayoutConfigs(
    sortedItems,
    originalItems,
    orders.mobile_and_above,
    widths.mobile_and_above,
    layoutConfigs.mobile_and_above
  );
  responsiveSortedOrders.mobile_and_above = mobileResult.sortedOrders;
  responsiveSortedWidths.mobile_and_above = mobileResult.sortedWidths;
  responsiveSortedConfigs.mobile_and_above = mobileResult.sortedConfigs;

  const tabletResult = getSortedOrdersAndWidthsAndLayoutConfigs(
    sortedItems,
    originalItems,
    orders.tablet_and_above,
    widths.tablet_and_above,
    layoutConfigs.tablet_and_above
  );
  responsiveSortedOrders.tablet_and_above = tabletResult.sortedOrders;
  responsiveSortedWidths.tablet_and_above = tabletResult.sortedWidths;
  responsiveSortedConfigs.tablet_and_above = tabletResult.sortedConfigs;

  const desktopResult = getSortedOrdersAndWidthsAndLayoutConfigs(
    sortedItems,
    originalItems,
    orders.desktop_and_above,
    widths.desktop_and_above,
    layoutConfigs.desktop_and_above
  );
  responsiveSortedOrders.desktop_and_above = desktopResult.sortedOrders;
  responsiveSortedWidths.desktop_and_above = desktopResult.sortedWidths;
  responsiveSortedConfigs.desktop_and_above = desktopResult.sortedConfigs;

  const xlargeResult = getSortedOrdersAndWidthsAndLayoutConfigs(
    sortedItems,
    originalItems,
    orders.x_large_and_above,
    widths.x_large_and_above,
    layoutConfigs.x_large_and_above
  );
  responsiveSortedOrders.x_large_and_above = xlargeResult.sortedOrders;
  responsiveSortedWidths.x_large_and_above = xlargeResult.sortedWidths;
  responsiveSortedConfigs.x_large_and_above = xlargeResult.sortedConfigs;

  return {
    responsiveSortedOrders,
    responsiveSortedWidths,
    responsiveSortedConfigs,
  };
};

// Sort orders, widths, and layout configs to match the sorted items
const getSortedOrdersAndWidthsAndLayoutConfigs = (
  sortedItems: SDUILayoutRendererItem[],
  originalItems: SDUILayoutRendererItem[],
  orders: number[] | undefined,
  widths: (number | undefined)[] | undefined,
  layoutConfigs: IResponsiveComponentLayoutConfig[] | undefined
): {
  sortedOrders: number[] | undefined;
  sortedWidths: (number | undefined)[] | undefined;
  sortedConfigs: IResponsiveComponentLayoutConfig[] | undefined;
} => {
  if (
    !orders ||
    !widths ||
    !layoutConfigs ||
    orders.length === 0 ||
    widths.length === 0 ||
    layoutConfigs.length === 0
  ) {
    return {
      sortedOrders: orders,
      sortedWidths: widths,
      sortedConfigs: layoutConfigs,
    };
  }

  // Create a mapping from original item to its position in the sorted array
  const itemPositionMap = new Map<SDUILayoutRendererItem, number>();
  sortedItems.forEach((item, index) => {
    itemPositionMap.set(item, index);
  });

  // Create arrays to hold the sorted orders and widths
  const sortedOrders = new Array(orders.length);
  const sortedWidths = new Array(widths.length);
  const sortedConfigs = new Array(layoutConfigs.length);

  // For each original item, place its order and width at the new position and update the layout config
  originalItems.forEach((item, originalIndex) => {
    if (originalIndex < orders.length && originalIndex < widths.length) {
      const newPosition = itemPositionMap.get(item);
      if (newPosition !== undefined) {
        sortedOrders[newPosition] = orders[originalIndex];
        sortedWidths[newPosition] = widths[originalIndex];
      }
    }

    const originalComponentIndex =
      layoutConfigs[originalIndex]?.component_index;
    const originalItem =
      originalComponentIndex !== undefined
        ? originalItems[originalComponentIndex]
        : undefined;
    if (originalItem !== undefined) {
      const newComponentIndex = itemPositionMap.get(originalItem);
      if (newComponentIndex !== undefined) {
        sortedConfigs[originalIndex] = IResponsiveComponentLayoutConfig.build({
          ...layoutConfigs[originalIndex],
          component_index: newComponentIndex,
        });
      }
    }
  });

  return { sortedOrders, sortedWidths, sortedConfigs };
};
